import { SortDirectionType } from 'src/app/shared/common/enums/sql-operators/sort-direction.enum';
import { User } from 'src/app/shared/user/user';
import { TCFile } from '../common/files/TCFile';

export class MapJob {
    id: string;
    bounds: L.LatLngBounds;
}
export class SurveyJob {
    // Identifiers
    id: string;
    eTag: string;

    // Properties
    name: string;
    description: string;
    assignees: User[];
    priority: SurveyJobPriority;
    status: SurveyJobStatus;
    lifeState: SurveyJobLifeState;
    rootDataFile: SurveyJobRootDataFile = {} as SurveyJobRootDataFile; // root data file object
    tags: string[];
    createdBy: User;
    modifiedBy: User;
    createdDateTime: string | Date;
    modifiedDateTime: string | Date;

    // Similar types
    attachments?: SurveyJobAttachment[] = [];
    linkedFiles?: Partial<SurveyJobAttachment>[] = [];

    // UI properties
    visible: boolean = false;

    // Constructor can be initialized with {} or with a SurveyJob object
    // for example: new SurveyJob({ id: 1, name: 'Survey 1' })
    constructor(data?: Partial<SurveyJob>) {
        if (data) {
            Object.assign(this, data);
        }
    }

    // add additional survey-job helpers methods below if needed
}

// These enums came from actual data coming for Jobs Service
export enum SurveyJobStatus {
    New = 'NEW',
    InProgress = 'IN_PROGRESS',
    FieldWorkComplete = 'RESOLVED',
    Closed = 'CLOSED',
    Blocked = 'BLOCKED',
    Unknown = null
}

export enum SurveyJobFilterId {
    createdUsers = 'createdUsers',
    assignedUsers = 'assignedUsers',
    status = 'status',
    tags = 'tags',
    createdFromDate = 'createdFromDate',
    createdToDate = 'createdToDate',
    updatedFromDate = 'updatedFromDate',
    updatedToDate = 'updatedToDate'
}

export enum SurveyJobPriority {
    Low = 'LOW',
    Medium = 'MEDIUM',
    High = 'HIGH',
    Critical = 'CRITICAL'
}

export enum SurveyJobLifeState {
    Active = 'ACTIVE',
    Archived = 'ARCHIVED',
    Deleted = 'DELETED'
}

export type SurveyJobRootDataFile = {
    attachmentId: string;
    fileName: string;
    downloadUrl: string;
    eTag: string;
    version: string;
    tags: string[];
    size: number;
    revision: number;
    hash: string;
    createdBy: User;
    modifiedBy: User;
    modifiedDateTime: string | Date;
    createdDateTime: string | Date;
};

// Note: https://jira.trimble.tools/browse/TCMAPS-4590?focusedCommentId=10940861 - Supported Filter / Sort Survey Job Fields
export enum SurveyJobSupportedFields {
    Id = 'Id',
    Name = 'Name',
    AssigneeUserId = 'AssigneeUserId',
    AssigneeName = 'AssigneeName',
    AssigneeEmail = 'AssigneeEmail',
    AssigneeType = 'AssigneeType',
    Priority = 'Priority',
    Status = 'Status',
    Tags = 'Tags',
    CreatedUtc = 'CreatedUtc',
    UpdatedUtc = 'UpdatedUtc',
    CreatedByName = 'CreatedByName',
    ModifiedByName = 'ModifiedByName',
    CreatedByUserId = 'CreatedByUserId',
    ModifiedByUserId = 'ModifiedByUserId'
}

/**
 * Subset of SurveyJobSupportedFields specifically for loading 'job list' operations
 * Uses the same field values to maintain consistency
 */
export enum JobListSupportedFields {
    AssigneeUserId = SurveyJobSupportedFields.AssigneeUserId,
    AssigneeName = SurveyJobSupportedFields.AssigneeName,
    AssigneeEmail = SurveyJobSupportedFields.AssigneeEmail,
    AssigneeType = SurveyJobSupportedFields.AssigneeType,
    Priority = SurveyJobSupportedFields.Priority,
    Status = SurveyJobSupportedFields.Status,
    Tags = SurveyJobSupportedFields.Tags,
    Assignee = 'Assignee'
}

export type SurveyJobSortOrder = `${SurveyJobSupportedFields} ${SortDirectionType}`;

// Optionally, you can pass filter and sortBy option:
//  -filter: string to filter survey jobs (e.g., `$Status EQ 'InProgress'`)
// - sortBy: SurveyJobSortOrder to sort survey jobs (default is `UpdatedUtc DESC`)
// - pagination: {
//    page: number to specify the current page
//    pageSize: number to specify the number of items per page (default is 5)
//    totalItems: number to specify the total number of items for pagination
//  }

// NOTE: Use Partial<SurveyJobListOptions> to allow partial updates and prevent ? per each property
export type SurveyJobListOptions = {
    filter: string; // e.g. `$Status IN ('InProgress', 'New')`
    sortBy: SurveyJobSortOrder; // e.g. string 'CreatedUtc DESC'
    pagination: {
        page: number; // current page number
        pageSize: number; // items per page
        totalItems: number;
    };
    fields: string; // comma separated SurveyJobSupportedFields
};

export type SurveyJobAttachment = {
    fileName: string;
    downloadUrl: string;
    version: string;
    tags?: string[];
    size: number;
    revision: number;
    hash?: string;
    createdBy?: User;
    modifiedBy?: User;
    modifiedDateTime: string;
    createdDateTime: string;
    links: any[];
    id: string;
    eTag?: string;
};

export type JobChipData = {
    id?: string;
    url?: string;
    label?: string;
};

export type SurveyJobCreateData = {
    name: string;
    description: string;
    assigneeUserId: string;
    tags: Array<string>;
    linkedFiles: Array<string>;
};

export type SurveyJobPatchData = {
    [SurveyJobPatchDataKey.Assignee]?: { id?: string };
    [SurveyJobPatchDataKey.Tags]?: Array<string>;
    [SurveyJobPatchDataKey.Status]?: SurveyJobStatus;
    [SurveyJobPatchDataKey.LinkedFiles]?: Array<string>;
};

export enum SurveyJobPatchDataKey {
    Assignee = 'assignee',
    Tags = 'tags',
    Status = 'status',
    LinkedFiles = 'linkedFiles'
}

export const mapToSurveyJobAttachment = (files: Partial<TCFile[]>): SurveyJobAttachment[] => {
    return files.map(
        file =>
            ({
                id: file.id,
                fileName: file.name,
                downloadUrl: null,
                version: file.versionId ?? null,
                revision: null,
                size: null,
                modifiedDateTime: null,
                createdDateTime: null,
                tags: [],
                links: []
            } as SurveyJobAttachment)
    );
};

/**
 * Common file extension collections
 */
export const SurveyJobFileExtensions = {
    IMAGES: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff', '.svg']
};
